import { useQuery } from "@tanstack/react-query";
import {
  RENTALS_QUERY_KEYS,
  RENTALS_STALE_TIME,
} from "@/features/rentals/configs/rentalsQueryKeys.config";
import {
  getMyRentalBookings,
  getRentalAccountStatement,
  getRentalBooking,
} from "@/features/rentals/services/rentals.service";

export const useRentalAccountStatement = () =>
  useQuery({
    queryKey: RENTALS_QUERY_KEYS.accountStatement,
    queryFn: () => getRentalAccountStatement(),
    staleTime: RENTALS_STALE_TIME,
  });

type UseMyRentalBookingsOptions = {
  /** Disable the query — useful pour les orgs sans `service_catalog`
   * (évite un 403 polluant sur chaque navigation). Default `true`. */
  enabled?: boolean
};

// `myBookings` alimente le compteur de panier UnifiedCart (filtre
// `status: open`). On veut que tout retour sur le
// catalogue après un checkout (paid / pay_later) ait des données
// fraîches : si on garde `staleTime` du contexte (60 s), un benef
// qui finalise puis retourne au catalogue dans la foulée voit son
// ancien booking encore en « panier » jusqu'à la prochaine fenêtre
// stale — alors qu'il a déjà été transitionné côté API.
const MY_BOOKINGS_STALE_TIME = 0;

export const useMyRentalBookings = (options?: UseMyRentalBookingsOptions) => {
  return useQuery({
    queryKey: RENTALS_QUERY_KEYS.myBookings,
    queryFn: () => getMyRentalBookings(),
    staleTime: MY_BOOKINGS_STALE_TIME,
    // `always` plutôt que `true` : refetch même si une donnée
    // existe en cache, pour rattraper les transitions de statut
    // post-checkout qui auraient pu manquer une invalidation.
    refetchOnMount: "always",
    enabled: options?.enabled ?? true,
  });
};

export const useRentalBooking = (bookingId: string | undefined) => {
  return useQuery({
    queryKey: RENTALS_QUERY_KEYS.booking(bookingId),
    queryFn: () => (bookingId ? getRentalBooking(bookingId) : Promise.resolve(null)),
    enabled: Boolean(bookingId),
    staleTime: RENTALS_STALE_TIME,
  });
};
